/**
 * Starting a conversation, and what happens when the server says no
 * (29 Sep 2026).
 *
 * It used to go through useCrudApi, which swallows a refusal and leaves its
 * item as whatever it held before, so a refused start could hand back the
 * PREVIOUS conversation and open it. Now the call throws (httpRequest), the
 * temporary copy written for the optimistic list is removed, the server's own
 * sentence is what the member reads ("You can't message this person.", "You
 * can only message someone who is in one of your clubs."), and no chat opens.
 *
 * Pure, imported explicitly, tested directly.
 */

export const CONVERSATION_FAILED_TEXT = "Couldn't start the conversation. Try again.";

/** The server's first `global` line, else the generic sentence. */
export const conversationFailureText = (error) =>
    error?.global?.[0] || error?.data?.global?.[0] || CONVERSATION_FAILED_TEXT;

/** A reply is a conversation only if it carries one. */
export const isConversationReply = (reply) => typeof reply?.id === 'string' && reply.id.length > 0;

/**
 * Adds the temporary conversation, asks the server, and either swaps the
 * temporary one for the real one or takes it away again. Throws an Error whose
 * message is what the member should read; `global` carries the server's lines.
 */
export async function startConversation({temporary, addTemporary, removeTemporary, replaceTemporary, callApi}) {
    await addTemporary(temporary);
    let reply;
    try {
        reply = await callApi();
        if (!isConversationReply(reply)) {
            throw {global: [CONVERSATION_FAILED_TEXT]};
        }
    } catch (error) {
        try {
            await removeTemporary(temporary.id);
        } catch {
            // The list is reloaded by the caller either way.
        }
        const failure = new Error(conversationFailureText(error));
        failure.global = [failure.message];
        failure.data = error;
        throw failure;
    }
    await replaceTemporary(temporary.id, reply);
    return reply;
}
